import { canAccessSettings, OrganizationModule, useAuthStore } from '@/entities/auth';
import {
  IconCalendar,
  IconUsers,
  IconSettings,
  IconMusic,
  IconFile,
  IconMessage,
  IconMegaphone,
  IconReceipt,
  IconStore,
  IconTire,
  IconChartBars,
} from '@/shared/ui/icons';
import { todayInTimezone } from '@shared/lib/date-time';

import type { MenuItem, MenuLink } from '../model/menu-types';
import type { MenuAccess } from '../model/menu-visibility';

import { getVisibleMenuItems } from '../model/menu-visibility';

export const menuItems: MenuItem[] = [
  {
    id: 'booking',
    label: 'Запись',
    get to() {
      return {
        name: 'booking.chessboard',
        params: { date: todayInTimezone(useAuthStore().currentLocation?.timezone)?.toString() },
      };
    },
    icon: IconCalendar,
    module: OrganizationModule.Booking,
    requiresLocation: true,
  },
  {
    id: 'clients',
    label: 'Клиенты',
    to: { name: 'clients.index' },
    icon: IconUsers,
    module: OrganizationModule.Clients,
    permission: 'clients.detail',
  },
  {
    type: 'group',
    id: 'tire-service',
    label: 'Шиномонтаж',
    icon: IconTire,
    module: OrganizationModule.TireService,
    children: [
      {
        id: 'tire-work-orders',
        label: 'Заказ-наряды',
        to: { name: 'tire-service.work-orders' },
        permission: 'tire_service.view',
        requiresLocation: true,
      },
      {
        id: 'tire-storages',
        label: 'Хранение',
        to: { name: 'tire-service.storages' },
        module: OrganizationModule.TireStorage,
        permission: 'tire_service.view',
        requiresLocation: true,
      },
    ],
  },
  {
    type: 'group',
    id: 'finance',
    label: 'Финансы',
    icon: IconReceipt,
    children: [
      {
        id: 'payments',
        label: 'Платежи',
        to: { name: 'payments.index' },
        permissions: ['payments.view', 'payments.accept', 'fiscalization.manage'],
        badge: 'finance-decisions',
      },
      {
        id: 'shifts',
        label: 'Смены',
        to: { name: 'shifts.index' },
        module: OrganizationModule.Orders,
        permission: 'payments.view',
      },
    ],
  },
  {
    id: 'radio',
    label: 'Радио',
    to: { name: 'radio.index' },
    icon: IconMusic,
    module: OrganizationModule.Radio,
  },
  {
    id: 'sop',
    label: 'Регламенты',
    to: { name: 'sop.sections' },
    icon: IconFile,
    module: OrganizationModule.Sop,
    permission: 'sop.view',
  },
  {
    id: 'messenger',
    label: 'Мессенджер',
    to: { name: 'messenger.index' },
    icon: IconMessage,
    module: OrganizationModule.Messenger,
    permission: 'messenger.view',
    // Бейдж числа неотвеченных диалогов; число резолвит useMenuBadges через
    // useMessengerUnansweredCount (realtime по WebSocket).
    badge: 'messenger-unanswered',
  },
  // Модуль объявлен на пунктах, а не на группе: автоматизация уходит у всех
  // организаций и гейтится только правом, остальные пункты требуют модуль.
  {
    type: 'group',
    id: 'marketing',
    label: 'Маркетинг',
    icon: IconMegaphone,
    children: [
      {
        id: 'automation',
        label: 'Автоматизация',
        to: { name: 'automation.index' },
        permission: 'client_automations.manage',
      },
      {
        id: 'scenarios',
        label: 'Сценарии',
        to: { name: 'scenarios.index' },
        module: OrganizationModule.Marketing,
        permission: 'scenario.view',
      },
      {
        id: 'marketing-sources',
        label: 'Каналы привлечения',
        to: { name: 'marketing-sources.index' },
        module: OrganizationModule.Marketing,
        permission: 'marketing_source.view',
      },
      {
        id: 'broadcasts',
        label: 'Рассылки',
        to: { name: 'broadcasts.index' },
        module: OrganizationModule.Marketing,
        permission: 'marketing.broadcasts.view',
      },
      {
        id: 'qr-links',
        label: 'QR-коды',
        to: { name: 'qr-links.index' },
        module: OrganizationModule.Marketing,
        permission: 'qr_link.view',
      },
    ],
  },
  {
    type: 'group',
    id: 'shop',
    label: 'Магазин',
    icon: IconStore,
    module: OrganizationModule.Shop,
    children: [
      {
        id: 'shop-showcase',
        label: 'Витрина',
        to: { name: 'shop.showcase' },
        permission: 'payments.accept',
        requiresLocation: true,
      },
      {
        id: 'shop-stock',
        label: 'Склад',
        to: { name: 'shop.stock' },
        permission: 'shop.receive',
        requiresLocation: true,
      },
      {
        id: 'shop-products',
        label: 'Каталог',
        to: { name: 'shop.products.index' },
        permission: 'shop.view',
      },
    ],
  },
  {
    type: 'group',
    id: 'reports',
    label: 'Отчёты',
    icon: IconChartBars,
    children: [
      {
        id: 'analytics',
        label: 'Ключевые показатели',
        to: { name: 'analytics.index' },
        module: OrganizationModule.Analytics,
        permission: 'analytics.view',
      },
      {
        id: 'reports-loyalty-registrations',
        label: 'Регистрации в бонусной программе',
        to: { name: 'reports.loyalty-registrations' },
        permission: 'reports.view',
      },
      {
        id: 'reports-new-client-retention',
        label: 'Возвращаемость новичков',
        to: { name: 'reports.new-client-retention' },
        permission: 'analytics.view',
      },
      {
        id: 'reports-regular-clients',
        label: 'Постоянные клиенты',
        to: { name: 'reports.regular-clients' },
        permission: 'analytics.view',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Настройки',
    icon: IconSettings,
    to: { name: 'settings.index' },
    visibleWhen: canAccessSettings,
  },
];

const firstLink = (items: MenuItem[]): MenuLink | null => {
  for (const item of items) {
    if (item.type === 'group') {
      const child = item.children[0];
      if (child) {
        return child;
      }

      continue;
    }

    return item;
  }

  return null;
};

export const getFirstAvailableMenuTarget = (access: MenuAccess): MenuLink['to'] | null => {
  const link = firstLink(getVisibleMenuItems(menuItems, access));

  if (!link) {
    return null;
  }

  if (link.id === 'booking') {
    return {
      name: 'booking.chessboard',
      params: { date: todayInTimezone(useAuthStore().currentLocation?.timezone)?.toString() },
    };
  }

  return link.to;
};
